import { ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { take, takeUntil, first } from 'rxjs/operators';
import * as fromCarriers from '@app/pages/delivery-service/store/reducers';
import { CarriersActions } from '@app/pages/delivery-service/store/actions';
import { Carrier, CarrierFields, CarrierTypes } from '@app/services/carriers/carrier.model';
import { CarriersService } from '@app/pages/delivery-service/services';
import { ShopService } from '@app/services/shops/shop.service';
import { CarrierFormComponent } from '../carrier-form/carrier-form.component';
import { normalizeCurrencyCode } from '@utils/currency-utils';

@Component({
  selector: 'app-edit-carrier',
  templateUrl: './edit-carrier.component.html',
  styleUrls: ['./edit-carrier.component.scss'],
  standalone: false,
})
export class EditCarrierComponent implements OnInit, OnDestroy {
  @ViewChild(CarrierFormComponent) carrierForm: CarrierFormComponent;
  @Input() editableCarrierType: CarrierTypes;
  @Input() editableCarrierID: number;
  @Input() shopID: number;
  @Input() duplicateMode: boolean = false;
  @Output() isPending = new EventEmitter<boolean>();
  @Output() closeAfterAction = new EventEmitter();

  pending$ = this.store.select(fromCarriers.selectPendingEditCarrier);
  errorMessage$ = this.store.select(fromCarriers.selectErrorMessageEditCarrier);
  destroyed$ = new Subject<boolean>();
  formFieldsLoaded: boolean = false;
  editableCarrier$: Observable<Carrier>;
  editableCarrierSubscription$: Subscription;
  editableCarrier: Carrier;
  editableCarrierFields: CarrierFields;
  ianaTimeZone: string = 'Asia/Jerusalem';
  isOnTrialPlan: boolean = false;

  readonly submitButton = 'Save';

  constructor(
    private store: Store<fromCarriers.State>,
    private carriersService: CarriersService,
    private shopService: ShopService,
    private changeDetectorRef: ChangeDetectorRef,
    private updates$: Actions,
  ) {
    this.subscribeOnEditCarrierSuccess();
  }

  ngOnInit() {
    this.loadEditableCarrierFields(this.editableCarrierType);
    this.loadEditableCarrier(this.editableCarrierID);
    this.initShopService();
  }

  initShopService() {
    this.shopService.shop$.pipe(first()).subscribe((currentShop) => {
      this.isOnTrialPlan = currentShop?.isOnTrialPlan;
      this.ianaTimeZone = currentShop?.metadata?.ianaTimeZone;
    });
  }

  ngOnDestroy(): void {
    this.editableCarrierSubscription$?.unsubscribe();
    this.destroyed$?.next(true);
    this.destroyed$?.complete();
  }

  onSubmitForm(values: { carrier: Carrier; metadata?: { reauthenticate?: boolean } }): void {
    if (this.duplicateMode) {
      this.duplicateCarrier(values.carrier);
    } else {
      this.editCarrier({ id: this.editableCarrier.id, ...values.carrier }, values.metadata);
    }
  }

  onSubmit(payload?: { reauthenticate?: boolean }): void {
    this.carrierForm.onSubmit(payload);
  }

  private subscribeOnEditCarrierSuccess() {
    this.updates$
      .pipe(ofType(CarriersActions.editCarrierSuccess), takeUntil(this.destroyed$), take(1))
      .subscribe(() => {
        this.loadCarriers();
        this.isPending.emit(false);
        this.closeAfterAction.emit();
      });
  }

  private loadCarriers(): void {
    this.store.dispatch(CarriersActions.loadCarriersByShop({ shopID: this.shopID }));
  }

  private loadEditableCarrierFields(carrierType: CarrierTypes) {
    this.carriersService.getCarrierFieldsConfig(carrierType.name, this.editableCarrierID).subscribe((data) => {
      this.formFieldsLoaded = true;
      this.editableCarrierFields = data;
      this.changeDetectorRef.detectChanges();
    });
  }

  private loadEditableCarrier(carrierID: number) {
    this.editableCarrier$ = this.carriersService.loadCarrierByID(carrierID, this.shopID);
    this.editableCarrierSubscription$ = this.editableCarrier$.subscribe((carrier: Carrier) => {
      if (carrier?.metadata) {
        const metadata = carrier.metadata.map((field) => {
          if (field.fieldName === 'currencyCode' && typeof field.default === 'string') {
            return { ...field, default: normalizeCurrencyCode(field.default) };
          }
          return field;
        });
        carrier = { ...carrier, metadata };
      }

      this.editableCarrier = carrier;
      this.changeDetectorRef.detectChanges();
    });
  }

  private editCarrier(carrier: Carrier, metadata?: { reauthenticate?: boolean }): void {
    this.isPending.emit(true);
    this.store.dispatch(CarriersActions.editCarrier({ carrier, shopID: this.shopID, metadata: metadata }));
  }

  private duplicateCarrier(carrier: Carrier): void {
    this.isPending.emit(true);
    // remove id from providers & provider services to create new ones:
    carrier.providers = (carrier.providers || []).map(({ id, ...providerFields }) => {
      providerFields.providerServices = providerFields.providerServices.map(({ id, ...providerServiceFields }) => ({
        ...providerServiceFields,
      })) as any;
      return { ...providerFields };
    }) as any;
    this.store.dispatch(CarriersActions.addNewCarrier({ carrier, shopID: this.shopID }));
  }
}
